import type { LinearScale } from './scale'

/**
 * SVG path construction for the fan.
 *
 * Paths are built from numeric arrays every frame rather than interpolated as
 * strings. Lerping two `d` strings would mean parsing them back into numbers on
 * every frame; lerping the numbers and serialising once is both faster and the
 * only way to guarantee the result is still a valid path.
 */

/**
 * Anything indexable by number. Lets the path builders take a plain array (the
 * age axis) and a Float32Array (every animated series) without either side
 * copying to satisfy the other.
 */
export interface NumberList {
  readonly length: number
  readonly [index: number]: number
}

/** One decimal is below a device pixel at any sane size, and it halves the string length. */
function px(value: number): string {
  return (Math.round(value * 10) / 10).toString()
}

/**
 * A closed band between `lower` and `upper`: forward along the top, back along
 * the bottom. Both arrays must be the same length as `xs`.
 */
export function bandPath(
  xs: NumberList,
  lower: NumberList,
  upper: NumberList,
  sx: LinearScale,
  sy: LinearScale,
): string {
  const n = xs.length
  if (n === 0) return ''

  const parts: string[] = []
  for (let i = 0; i < n; i++) {
    parts.push(`${i === 0 ? 'M' : 'L'}${px(sx(xs[i]))},${px(sy(upper[i]))}`)
  }
  for (let i = n - 1; i >= 0; i--) {
    parts.push(`L${px(sx(xs[i]))},${px(sy(lower[i]))}`)
  }
  parts.push('Z')
  return parts.join('')
}

/** An open polyline — the median path and the requirement threshold. */
export function linePath(
  xs: NumberList,
  ys: NumberList,
  sx: LinearScale,
  sy: LinearScale,
): string {
  const n = xs.length
  if (n === 0) return ''

  const parts: string[] = []
  for (let i = 0; i < n; i++) {
    parts.push(`${i === 0 ? 'M' : 'L'}${px(sx(xs[i]))},${px(sy(ys[i]))}`)
  }
  return parts.join('')
}

/**
 * Moves `current` a fraction of the way to `target`, in place.
 *
 * `k` is derived per frame from elapsed time rather than being a fixed constant,
 * so the settle takes the same wall-clock time at 120Hz as it does at 30Hz — a
 * fixed per-frame fraction would animate twice as fast on a ProMotion display.
 *
 * Returns true once every component has effectively arrived, so the caller can
 * stop the animation loop instead of burning frames on imperceptible deltas.
 */
export function lerpInto(
  current: Float32Array,
  target: Float32Array,
  k: number,
  epsilon: number,
): boolean {
  let settled = true
  for (let i = 0; i < current.length; i++) {
    const delta = target[i] - current[i]
    if (delta > epsilon || delta < -epsilon) {
      current[i] += delta * k
      settled = false
    } else {
      current[i] = target[i]
    }
  }
  return settled
}

/**
 * Time-corrected smoothing factor for an exponential approach.
 *
 * `tau` is the time constant in milliseconds — roughly, the value covers 63% of
 * the remaining distance every `tau`. Around 90ms reads as immediate but still
 * visibly physical; much lower and the fan snaps, much higher and dragging a
 * slider feels laggy even though the maths already finished.
 */
export function smoothingFactor(deltaMs: number, tau: number): number {
  return 1 - Math.exp(-deltaMs / tau)
}
